import { useRef, useState } from 'react';
import styles from './index.module.css';
import logo from '../../../../assets/images/logo.svg';

interface VideoData {
  videoUrl: string;
  thumbnailUrl?: string;
  time: string;
}

interface VideoItemProps {
  video: VideoData;
  courtLabel?: string;
  dateLabel?: string;
  /** 'ft' = paleta azul/verde da página FastTennis (padrão: laranja Apertai) */
  variant?: 'default' | 'ft';
}

const ShareIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 4v11m0 0 4-4m-4 4-4-4" />
    <path d="M5 19h14" />
  </svg>
);

function VideoItem({ video, courtLabel, dateLabel, variant = 'default' }: VideoItemProps) {
  const [active, setActive] = useState(false); // usuário clicou em tocar
  const [started, setStarted] = useState(false); // vídeo já está exibindo frames
  const [liked, setLiked] = useState(false);
  const [busy, setBusy] = useState(false);
  // thumbs podem chegar cruas (landscape, precisam girar) ou já em pé (portrait)
  const [posterUpright, setPosterUpright] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const poster = video.thumbnailUrl ?? logo;
  const fileName = `replay-${video.time.replace(':', 'h')}.mp4`;

  // Chamado DIRETO no clique (gesto do usuário) → toca de primeira no celular, com som.
  const play = () => {
    setActive(true);
    const v = videoRef.current;
    if (v) {
      v.play().catch(() => {});
    }
  };

  const stop = () => {
    const v = videoRef.current;
    if (v) {
      v.pause();
      v.currentTime = 0;
    }
    setActive(false);
    setStarted(false);
  };

  // Baixa o vídeo de fato (blob), sem redirecionar para a página do Storage.
  const handleDownload = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const response = await fetch(video.videoUrl);
      if (!response.ok) throw new Error('fetch failed');
      const blob = await response.blob();
      if (blob.size === 0) throw new Error('empty');
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      // Fallback (ex.: CORS): abre o link direto numa nova aba.
      const link = document.createElement('a');
      link.href = video.videoUrl;
      link.download = fileName;
      link.target = '_blank';
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setBusy(false);
    }
  };

  const handleShare = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (busy) return;

    // Sem Web Share (desktop): baixa o vídeo.
    if (typeof navigator === 'undefined' || !navigator.share) {
      handleDownload(event);
      return;
    }

    setBusy(true);
    try {
      // 1) Compartilha o ARQUIVO do vídeo (ideal no celular: WhatsApp, IG...).
      if (navigator.canShare) {
        try {
          const response = await fetch(video.videoUrl);
          if (response.ok) {
            const blob = await response.blob();
            const file = new File([blob], fileName, { type: 'video/mp4' });
            if (blob.size > 0 && navigator.canShare({ files: [file] })) {
              await navigator.share({
                files: [file],
                title: `Replay ${video.time}`,
                text: 'Confira meu replay! 🎾',
              });
              return;
            }
          }
        } catch (error: any) {
          if (error?.name === 'AbortError') return; // usuário fechou o modal
          // fetch falhou → cai para o compartilhamento por link (ainda abre o modal)
        }
      }

      // 2) Fallback: compartilha um link — o modal nativo ainda abre.
      await navigator.share({
        title: `Replay ${video.time}`,
        text: 'Confira meu replay! 🎾',
        url: video.videoUrl,
      });
    } catch (error: any) {
      if (error?.name !== 'AbortError') handleDownload(event);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${styles.card} ${variant === 'ft' ? styles.ft : ''}`}>
      {/* Vídeo sempre montado, mas preload="none": NADA é baixado até tocar (economia de banda) */}
      <video
        ref={videoRef}
        className={styles.video}
        style={{ opacity: started ? 1 : 0 }}
        src={video.videoUrl}
        preload="none"
        controls={active}
        playsInline
        onPlaying={() => setStarted(true)}
      />

      {/* Thumbnail (orientação certa) fica por cima até o vídeo começar */}
      {!started && (
        <img
          className={`${styles.poster} ${posterUpright ? styles.poster_upright : ''}`}
          src={poster}
          alt={`Replay ${video.time}`}
          loading="lazy"
          onLoad={(e) => {
            const img = e.currentTarget;
            if (img.naturalHeight > img.naturalWidth) setPosterUpright(true);
          }}
        />
      )}

      {/* Spinner enquanto o vídeo carrega após o clique */}
      {active && !started && (
        <div className={styles.loading}>
          <span className={styles.spinner} />
        </div>
      )}

      {active ? (
        <>
          {/* Ações acessíveis enquanto assiste */}
          <div className={styles.play_actions}>
            <button className={styles.round_btn} onClick={handleShare} disabled={busy} aria-label="Compartilhar" title="Compartilhar">
              <ShareIcon />
            </button>
            <button className={styles.round_btn} onClick={handleDownload} disabled={busy} aria-label="Baixar" title="Baixar">
              <DownloadIcon />
            </button>
          </div>

          <button className={styles.close_btn} onClick={stop} aria-label="Fechar" title="Fechar">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </>
      ) : (
        <>
          <div className={styles.tap_layer} onClick={play}>
            <div className={styles.scrim} />

            <div className={styles.badge_replay}>
              <span className={styles.badge_dot} />
              <span className={styles.badge_label}>REPLAY</span>
            </div>

            <button className={styles.play_btn} aria-label="Reproduzir" title="Reproduzir">
              <span className={styles.pulse} />
              <span className={styles.play_disc}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff" style={{ marginLeft: 3 }}>
                  <path d="M8 5v14l11-7z" />
                </svg>
              </span>
            </button>

            <div className={styles.meta}>
              <div className={styles.time}>{video.time}</div>
              {(courtLabel || dateLabel) && (
                <div className={styles.sub}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11z" />
                    <circle cx="12" cy="10" r="2.4" />
                  </svg>
                  {courtLabel && <span>{courtLabel}</span>}
                  {courtLabel && dateLabel && <span className={styles.sub_sep}>·</span>}
                  {dateLabel && <span>{dateLabel}</span>}
                </div>
              )}
            </div>
          </div>

          <div className={styles.actions}>
            <button
              className={`${styles.icon_btn} ${liked ? styles.like_active : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                setLiked((v) => !v);
              }}
              aria-label="Curtir"
              title="Curtir"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill={liked ? '#ff5a6a' : 'none'} stroke="currentColor" strokeWidth="2">
                <path d="M12 21s-7.5-4.9-10-9.4C.6 8.8 2 5 5.6 5c2 0 3.4 1.2 4.4 2.6C11 6.2 12.4 5 14.4 5 18 5 19.4 8.8 22 11.6 19.5 16.1 12 21 12 21z" />
              </svg>
            </button>

            <button className={styles.icon_btn} onClick={handleDownload} disabled={busy} aria-label="Baixar" title="Baixar">
              <DownloadIcon />
            </button>

            <button className={styles.share_btn} onClick={handleShare} disabled={busy}>
              {busy ? (
                'Preparando...'
              ) : (
                <>
                  <ShareIcon />
                  Compartilhar
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default VideoItem;
